import {
  PASTED_TEXT_CARD_MIN_CHARACTERS,
  PASTED_TEXT_CARD_MIN_LINES,
  PASTED_TEXT_CLOSE_TAG,
  PASTED_TEXT_CODE_LINE_RATIO,
  PASTED_TEXT_JSON_PARSE_MAX_CHARACTERS,
  PASTED_TEXT_KIND_SAMPLE_LINES,
  PASTED_TEXT_LENGTH_ATTRIBUTE,
  PASTED_TEXT_MARKDOWN_LINE_RATIO,
  PASTED_TEXT_MESSAGE_SEPARATOR,
  PASTED_TEXT_SNIPPET_MAX_CHARACTERS,
  PASTED_TEXT_SNIPPET_MAX_LINES,
  PASTED_TEXT_TAG_NAME,
} from "../constants/pasted-text";
import {
  EPastedTextKind,
  type IPastedText,
  type IPastedTextSummary,
} from "../types/pasted-text";
import { createAttachmentId } from "./attachment-identity";
import type {
  IComposeMessageWithPastedTextsProps,
  IExtractPastedRunProps,
  IMessagePastedTexts,
  IPastedRun,
} from "./pasted-text.types";

// NOTE: A line that only code tends to have: a statement or block terminator,
// a declaration keyword, a comment opener, an operator prose does not use, or
// a line that is a single markup tag. `*` comment continuations are left out on
// purpose — they are indistinguishable from a Markdown bullet.
const CODE_LINE_PATTERNS: readonly RegExp[] = [
  /[;{}]\s*$/,
  /^\s*(import|export|from|const|let|var|function|def|class|return|if|elif|else|for|while|switch|case|try|catch|except|finally|public|private|protected|static|package|using|namespace|fn|func|impl|struct|enum|interface|type|async|await|SELECT|INSERT|UPDATE|DELETE|CREATE|ALTER|WITH)\b/,
  /^\s*(\/\/|\/\*|#!|#include|#define|<\?)/,
  /=>|::|->|===|!==|&&|\|\|/,
  /^\s*<\/?[A-Za-z][\w:-]*(\s[^>]*)?\/?>\s*$/,
];

// NOTE: Block-level Markdown: headings, bullets, numbered items, quotes and
// table rows.
const MARKDOWN_LINE_PATTERN =
  /^\s{0,3}(#{1,6}\s+\S|[-*+]\s+\S|\d+[.)]\s+\S|>\s?\S|\|.+\|\s*$)/;
const MARKDOWN_FENCE_PATTERN = /^\s{0,3}(```|~~~)/m;
const LEADING_BLANK_LINES_PATTERN = /^(?:[ \t]*\n)+/;

// NOTE: Clipboards on Windows (and some web apps) deliver "\r\n". The textarea
// would have normalised it on insert, so doing the same keeps a carded paste
// byte-for-byte what an inline paste of the same text would have sent.
export const normalizePastedText = (text: string): string =>
  text.replace(/\r\n?/g, "\n");

// NOTE: The number of lines a person would count — a final line break does not
// start another line.
export const countTextLines = (text: string): number => {
  if (text.length === 0) return 0;
  const lineBreaks = text.match(/\n/g)?.length ?? 0;
  return text.endsWith("\n") ? lineBreaks : lineBreaks + 1;
};

// NOTE: NSAI-6791 — whether a paste becomes a card. Whitespace alone never
// does: a card that shows nothing is worse than the blank lines it hides.
export const isLongPastedText = (text: string): boolean => {
  if (text.trim().length === 0) return false;
  if (text.length >= PASTED_TEXT_CARD_MIN_CHARACTERS) return true;
  return countTextLines(text) >= PASTED_TEXT_CARD_MIN_LINES;
};

const isJsonDocument = (trimmed: string): boolean => {
  const isObject = trimmed.startsWith("{") && trimmed.endsWith("}");
  const isArray = trimmed.startsWith("[") && trimmed.endsWith("]");
  if (!isObject && !isArray) return false;
  // NOTE: The summary is recomputed on every edit of a card, so a very large
  // bracketed document is labelled by its shape rather than parsed in full on
  // each keystroke. A wrong guess only changes the label and the wrap mode.
  if (trimmed.length > PASTED_TEXT_JSON_PARSE_MAX_CHARACTERS) return true;
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
};

export const inferPastedTextKind = (text: string): EPastedTextKind => {
  const trimmed = text.trim();
  if (trimmed.length === 0) return EPastedTextKind.TEXT;
  if (isJsonDocument(trimmed)) return EPastedTextKind.JSON;
  // NOTE: A fence means a document that CONTAINS code — Markdown, even when
  // most of its lines are the code inside the fences.
  if (MARKDOWN_FENCE_PATTERN.test(trimmed)) return EPastedTextKind.MARKDOWN;

  const sampledLines = trimmed
    .split("\n", PASTED_TEXT_KIND_SAMPLE_LINES)
    .filter((line) => line.trim().length > 0);
  const lineTotal = sampledLines.length;
  const codeRatio =
    sampledLines.filter((line) =>
      CODE_LINE_PATTERNS.some((pattern) => pattern.test(line)),
    ).length / lineTotal;
  const markdownRatio =
    sampledLines.filter((line) => MARKDOWN_LINE_PATTERN.test(line)).length /
    lineTotal;

  const isCodeLike = codeRatio >= PASTED_TEXT_CODE_LINE_RATIO;
  const isMarkdownLike = markdownRatio >= PASTED_TEXT_MARKDOWN_LINE_RATIO;
  // NOTE: Python and shell comments read as headings, so when both shapes are
  // present the stronger signal wins.
  if (isCodeLike && (!isMarkdownLike || codeRatio >= markdownRatio)) {
    return EPastedTextKind.CODE;
  }
  if (isMarkdownLike) return EPastedTextKind.MARKDOWN;
  return EPastedTextKind.TEXT;
};

// NOTE: The first few lines that carry something, trimmed at the end and
// capped in length. Code points, not UTF-16 units, are counted, so the cut can
// never split an emoji or an astral character in half.
export const buildPastedTextSnippet = (text: string): string => {
  const lines = text
    .replace(LEADING_BLANK_LINES_PATTERN, "")
    .split("\n", PASTED_TEXT_SNIPPET_MAX_LINES)
    .map((line) => line.trimEnd());
  const joined = lines.join("\n").trimEnd();
  const codePoints = Array.from(joined);
  if (codePoints.length <= PASTED_TEXT_SNIPPET_MAX_CHARACTERS) return joined;
  return `${codePoints.slice(0, PASTED_TEXT_SNIPPET_MAX_CHARACTERS).join("").trimEnd()}…`;
};

export const summarizePastedText = (text: string): IPastedTextSummary => ({
  kind: inferPastedTextKind(text),
  lineCount: countTextLines(text),
  characterCount: text.length,
  snippet: buildPastedTextSnippet(text),
});

export const createPastedText = (content: string): IPastedText => ({
  id: createAttachmentId(),
  content: normalizePastedText(content),
});

// NOTE: NSAI-6791 — the opening marker carries the paste's exact length, and a
// block is read back only when that many characters are followed by the
// closing marker on its own line. Typing the two marker lines by hand, or a
// paste that itself contains a closing-marker line, therefore never reads as a
// card: the count would have to match to the character. A message whose body
// was changed after it was composed simply reads back as plain text.
export const buildPastedTextOpenTag = (characterCount: number): string =>
  `<${PASTED_TEXT_TAG_NAME} ${PASTED_TEXT_LENGTH_ATTRIBUTE}="${characterCount}">`;

const OPEN_TAG_LINE_PATTERN = new RegExp(
  `(?:^|\\n)<${PASTED_TEXT_TAG_NAME} ${PASTED_TEXT_LENGTH_ATTRIBUTE}="(\\d{1,9})">\\n`,
  "g",
);
const CLOSE_TAG_SUFFIX = `\n${PASTED_TEXT_CLOSE_TAG}`;

const wrapPastedText = (content: string): string =>
  `${buildPastedTextOpenTag(content.length)}\n${content}${CLOSE_TAG_SUFFIX}`;

// NOTE: NSAI-6791 — the one place the message that goes out is assembled. The
// instruction comes first, so a draft that opens with a pinned `/skill` still
// opens the message with it, then each block in the order it was pasted, every
// part exactly once and separated by a blank line. A block is sent verbatim —
// leading blank lines and trailing whitespace included — so the recipient gets
// exactly what the card held; only a block emptied to whitespace in the editor
// is dropped rather than sent as an empty wrapper.
export const composeMessageWithPastedTexts = ({
  instruction,
  pastedTexts,
}: IComposeMessageWithPastedTextsProps): string =>
  [
    instruction.trim(),
    ...pastedTexts
      .map((pastedText) => pastedText.content)
      .filter((content) => content.trim().length > 0)
      .map(wrapPastedText),
  ]
    .filter((part) => part.length > 0)
    .join(PASTED_TEXT_MESSAGE_SEPARATOR);

// NOTE: `\s` and `String.prototype.trim` strip the same set of characters, so a
// block with no match here is exactly a block composeMessageWithPastedTexts
// drops — tested without copying the block the way `trim()` would.
const NON_WHITESPACE_PATTERN = /\S/;

// NOTE: NSAI-5691 — the length composeMessageWithPastedTexts would return,
// worked out without building the message. The composer re-measures the draft
// on every keystroke to enforce MAX_CHAT_MESSAGE_LENGTH, and assembling a
// 100 000-character body just to read its length would copy every card on
// every key. Pinned to the composed string's length by tests, so the two
// cannot drift.
export const measureComposedMessageLength = ({
  instruction,
  pastedTexts,
}: IComposeMessageWithPastedTextsProps): number => {
  const partLengths = [
    instruction.trim().length,
    ...pastedTexts
      .map((pastedText) => pastedText.content)
      .filter((content) => NON_WHITESPACE_PATTERN.test(content))
      .map(
        (content) =>
          buildPastedTextOpenTag(content.length).length +
          1 +
          content.length +
          CLOSE_TAG_SUFFIX.length,
      ),
  ].filter((partLength) => partLength > 0);
  const separatorsLength =
    Math.max(partLengths.length - 1, 0) * PASTED_TEXT_MESSAGE_SEPARATOR.length;

  return partLengths.reduce(
    (totalLength, partLength) => totalLength + partLength,
    separatorsLength,
  );
};

// NOTE: NSAI-6791 — the reverse of composeMessageWithPastedTexts: the text the
// user typed (everything outside the markers) and each paste, verbatim. A
// message with no valid markers — every message sent before this feature, and
// any where the markers were typed by hand — comes back as its own text and no
// pastes, so it renders as it always did.
export const splitMessagePastedTexts = (
  content: string,
): IMessagePastedTexts => {
  const pastedTexts: string[] = [];
  const textParts: string[] = [];
  const openTagPattern = new RegExp(OPEN_TAG_LINE_PATTERN.source, "g");
  let cursor = 0;
  let match = openTagPattern.exec(content);

  while (match !== null) {
    const contentStart = match.index + match[0].length;
    const contentEnd = contentStart + Number(match[1]);
    const blockEnd = contentEnd + CLOSE_TAG_SUFFIX.length;
    const isWellFormed =
      contentEnd <= content.length &&
      content.startsWith(CLOSE_TAG_SUFFIX, contentEnd) &&
      (blockEnd === content.length || content.charAt(blockEnd) === "\n");

    if (isWellFormed) {
      textParts.push(content.slice(cursor, match.index));
      pastedTexts.push(content.slice(contentStart, contentEnd));
      cursor = blockEnd;
      openTagPattern.lastIndex = blockEnd;
    } else {
      openTagPattern.lastIndex = match.index + 1;
    }
    match = openTagPattern.exec(content);
  }

  if (pastedTexts.length === 0) return { text: content, pastedTexts };

  textParts.push(content.slice(cursor));
  const text = textParts
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return { text, pastedTexts };
};

// NOTE: The message as the user would expect to copy it: their text, then each
// paste, with the markers taken out.
export const stripPastedTextMarkers = (content: string): string => {
  const { text, pastedTexts } = splitMessagePastedTexts(content);
  if (pastedTexts.length === 0) return content;
  return [text, ...pastedTexts]
    .filter((part) => part.length > 0)
    .join(PASTED_TEXT_MESSAGE_SEPARATOR);
};

// NOTE: NSAI-6791 — React Native's TextInput has no paste event, so mobile
// learns of a paste only from the value it produced. This reads the one edit
// between two values: the longest common head and tail are what the edit did
// not touch, and what lies between them in `next` is what it put in.
//
// The tail is capped so the two never overlap. Where the pasted text begins
// with the very characters that follow the caret, the edit is ambiguous —
// "ab" pasted after the "a" of "ab" gives "aabb" whichever reading is right —
// and the cap resolves it to the right-most reading. Every append is that
// reading, and for a paste mid-text the alternative readings differ only by
// characters identical on both sides of the boundary, so `remainder` plus
// `inserted` always holds every character of `next`.
export const extractPastedRun = ({
  previous,
  next,
}: IExtractPastedRunProps): IPastedRun | null => {
  const sharedLength = Math.min(previous.length, next.length);

  let headLength = 0;
  while (
    headLength < sharedLength &&
    previous.charCodeAt(headLength) === next.charCodeAt(headLength)
  ) {
    headLength += 1;
  }

  let tailLength = 0;
  while (
    tailLength < sharedLength - headLength &&
    previous.charCodeAt(previous.length - 1 - tailLength) ===
      next.charCodeAt(next.length - 1 - tailLength)
  ) {
    tailLength += 1;
  }

  const insertedEnd = next.length - tailLength;
  if (insertedEnd <= headLength) return null;

  return {
    inserted: next.slice(headLength, insertedEnd),
    remainder: next.slice(0, headLength) + next.slice(insertedEnd),
  };
};
